import React, { useState, useEffect } from 'react';
import { contestantsService, ContestantWithSeason } from '../../services/contestants.service';
import { seasonsService, SeasonWithReality } from '../../services/seasons.service';
import { realitiesService, Reality } from '../../services/realities.service';

export function ContestantsPage() {
  const [contestants, setContestants] = useState<ContestantWithSeason[]>([]);
  const [seasons, setSeasons] = useState<SeasonWithReality[]>([]);
  const [realities, setRealities] = useState<Reality[]>([]);
  const [selectedReality, setSelectedReality] = useState<string>('all');
  const [selectedSeason, setSelectedSeason] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContestant, setEditingContestant] = useState<ContestantWithSeason | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    reality_id: '',
    season_id: '',
    full_name: '',
    photo_url: '',
    status: 'active' as 'active' | 'eliminated' | 'finalist' | 'winner',
    elimination_date: '',
    final_position: '',
  });

  useEffect(() => {
    loadRealities();
    loadSeasons();
    loadContestants();
  }, []);

  useEffect(() => {
    loadContestants();
  }, [selectedSeason]);

  useEffect(() => {
    // Cuando cambia el reality, resetear el filtro de temporada
    setSelectedSeason('all');
  }, [selectedReality]);

  const loadRealities = async () => {
    try {
      const data = await realitiesService.getAll();
      setRealities(data.filter(r => r.active));
    } catch (err: any) {
      setError(err.message || 'Error al cargar los realities');
    }
  };

  const loadSeasons = async () => {
    try {
      const data = await seasonsService.getAll();
      setSeasons(data.filter(s => s.active));
    } catch (err: any) {
      setError(err.message || 'Error al cargar las temporadas');
    }
  };

  const loadContestants = async () => {
    try {
      setLoading(true);
      let data;
      if (selectedSeason === 'all') {
        data = await contestantsService.getAll();
      } else {
        data = await contestantsService.getBySeason(selectedSeason);
      }
      setContestants(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar los concursantes');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (contestant?: ContestantWithSeason) => {
    if (contestant) {
      setEditingContestant(contestant);
      setFormData({
        reality_id: contestant.season?.reality_id || '',
        season_id: contestant.season_id,
        full_name: contestant.full_name,
        photo_url: contestant.photo_url || '',
        status: contestant.status || 'active',
        elimination_date: contestant.elimination_date || '',
        final_position: contestant.final_position?.toString() || '',
      });
    } else {
      setEditingContestant(null);
      setFormData({
        reality_id: selectedReality !== 'all' ? selectedReality : '',
        season_id: selectedSeason !== 'all' ? selectedSeason : '',
        full_name: '',
        photo_url: '',
        status: 'active',
        elimination_date: '',
        final_position: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingContestant(null);
    setFormData({
      reality_id: '',
      season_id: '',
      full_name: '',
      photo_url: '',
      status: 'active',
      elimination_date: '',
      final_position: '',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const contestantData = {
        season_id: formData.season_id,
        full_name: formData.full_name,
        photo_url: formData.photo_url || null,
        status: formData.status,
        elimination_date: formData.elimination_date || null,
        final_position: formData.final_position ? parseInt(formData.final_position) : null,
      };

      if (editingContestant) {
        await contestantsService.update(editingContestant.id, contestantData);
      } else {
        await contestantsService.create(contestantData);
      }
      await loadContestants();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'Error al guardar el concursante');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Estás seguro de eliminar este concursante?')) return;

    try {
      await contestantsService.delete(id);
      await loadContestants();
    } catch (err: any) {
      setError(err.message || 'Error al eliminar el concursante');
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      active: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
      eliminated: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
      finalist: 'bg-violet-100 dark:bg-violet-900/30 text-violet-800 dark:text-violet-300',
      winner: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
    };
    const labels = {
      active: 'Activo',
      eliminated: 'Eliminado',
      finalist: 'Finalista',
      winner: 'Ganador',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badges[status as keyof typeof badges]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  // Filtrar temporadas según el reality seleccionado en los filtros
  const getFilteredSeasons = () => {
    if (selectedReality === 'all') {
      return seasons;
    }
    return seasons.filter(s => s.reality_id === selectedReality);
  };

  // Filtrar temporadas según el reality seleccionado en el modal
  const getModalSeasons = () => {
    if (!formData.reality_id) {
      return [];
    }
    return seasons.filter(s => s.reality_id === formData.reality_id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Concursantes</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Gestiona los concursantes de cada temporada
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Agregar Concursante
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Reality Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Filtrar por reality
            </label>
            <select
              value={selectedReality}
              onChange={(e) => setSelectedReality(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
            >
              <option value="all">Todos los realities</option>
              {realities.map((reality) => (
                <option key={reality.id} value={reality.id}>
                  {reality.name}
                </option>
              ))}
            </select>
          </div>

          {/* Season Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Filtrar por temporada
            </label>
            <select
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
            >
              <option value="all">Todas las temporadas</option>
              {getFilteredSeasons().map((season) => (
                <option key={season.id} value={season.id}>
                  {selectedReality === 'all' && `${season.reality?.name} - `}
                  Temporada {season.number} ({season.year})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="space-y-4 md:space-y-0">
        {contestants.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              No hay concursantes registrados. Agrega uno para comenzar.
            </p>
          </div>
        ) : (
          <>
            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {contestants.map((contestant) => (
                <div
                  key={contestant.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow p-4"
                >
                  <div className="flex items-start gap-3">
                    {contestant.photo_url && (
                      <img
                        src={contestant.photo_url}
                        alt={contestant.full_name}
                        className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 dark:text-white text-lg">
                        {contestant.full_name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {contestant.season?.reality?.name} - T{contestant.season?.number}
                      </p>
                      <div className="flex items-center gap-3 mt-3">
                        {getStatusBadge(contestant.status || 'active')}
                        <button
                          onClick={() => handleOpenModal(contestant)}
                          className="text-violet-600 dark:text-violet-400 hover:text-violet-900 dark:hover:text-violet-300 text-sm font-medium"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(contestant.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 text-sm font-medium"
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Concursante
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Temporada
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Posición
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {contestants.map((contestant) => (
                    <tr key={contestant.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          {contestant.photo_url && (
                            <img
                              src={contestant.photo_url}
                              alt={contestant.full_name}
                              className="w-10 h-10 rounded-lg object-cover"
                            />
                          )}
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {contestant.full_name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        {contestant.season?.reality?.name} - T{contestant.season?.number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(contestant.status || 'active')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        {contestant.final_position || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleOpenModal(contestant)}
                          className="text-violet-600 dark:text-violet-400 hover:text-violet-900 dark:hover:text-violet-300 mr-4"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(contestant.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"
              onClick={handleCloseModal}
            />

            {/* Modal Content */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    {editingContestant ? 'Editar Concursante' : 'Nuevo Concursante'}
                  </h3>

                  <div className="space-y-4">
                    {/* Reality */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Reality *
                      </label>
                      <select
                        required
                        value={formData.reality_id}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            reality_id: e.target.value,
                            season_id: '' // Reset season when reality changes
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      >
                        <option value="">Selecciona un reality</option>
                        {realities.map((reality) => (
                          <option key={reality.id} value={reality.id}>
                            {reality.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Season */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Temporada *
                      </label>
                      <select
                        required
                        value={formData.season_id}
                        onChange={(e) => setFormData({ ...formData, season_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        disabled={!formData.reality_id}
                      >
                        <option value="">
                          {!formData.reality_id ? 'Selecciona un reality primero' : 'Selecciona una temporada'}
                        </option>
                        {getModalSeasons().map((season) => (
                          <option key={season.id} value={season.id}>
                            Temporada {season.number} ({season.year})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Full Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nombre Completo *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      />
                    </div>

                    {/* Photo URL */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL de la Foto
                      </label>
                      <input
                        type="url"
                        value={formData.photo_url}
                        onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      />
                    </div>

                    {/* Status */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Estado *
                      </label>
                      <select
                        required
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      >
                        <option value="active">Activo</option>
                        <option value="eliminated">Eliminado</option>
                        <option value="finalist">Finalista</option>
                        <option value="winner">Ganador</option>
                      </select>
                    </div>

                    {/* Elimination Date */}
                    {(formData.status === 'eliminated' || formData.status === 'finalist' || formData.status === 'winner') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Fecha de Eliminación
                        </label>
                        <input
                          type="date"
                          value={formData.elimination_date}
                          onChange={(e) => setFormData({ ...formData, elimination_date: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        />
                      </div>
                    )}

                    {/* Final Position */}
                    {(formData.status === 'finalist' || formData.status === 'winner') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Posición Final
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={formData.final_position}
                          onChange={(e) => setFormData({ ...formData, final_position: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-violet-600 text-base font-medium text-white hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                  >
                    {editingContestant ? 'Actualizar' : 'Crear'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="mt-3 w-full sm:mt-0 sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
